import { useEffect, useMemo, useRef, useState } from 'react'
import { TeamGroupSummary } from '@numeo/types-main-web-app'
import { fetchDriverTeamGroupBadges } from 'services/teamGroupsApi'

/** How many drivers one request asks about. The server accepts 200; this keeps the query string short. */
const BADGE_REQUEST_SIZE = 100

const NO_BADGES: ReadonlyMap<string, TeamGroupSummary> = new Map()

/** The `asked` entry standing for the whole-tenant request. Not a driver id, so it cannot collide. */
const ALL = 'all'

/**
 * The group badge of each driver a Box Truck list is showing, by fleet driver record id.
 *
 * Its own request, fired once the list has its rows — like the SMS badge counts on Nearby Drivers. The badge is
 * decoration: it must never be something the driver list waits for or fails with, so a failed request simply
 * draws no badge.
 *
 * Works for every role. The server answers only for drivers the caller may already see, so there is no
 * permission check here to drift from it.
 *
 * TWO MODES, because two list shapes need different things:
 *
 *  - a LIST OF IDS — a paged roster. Asks only about ids it has not asked about yet, so a list that grows by
 *    a page asks for that page alone.
 *  - `'all'` — every driver the caller may see, in ONE request on mount. For the SMS inbox, which holds its
 *    whole conversation list at once: a thousand ids do not fit a query string, and chunking them makes the
 *    server repeat its group reads per chunk for an answer it computes in memory anyway. The cost of this
 *    mode is the tenant's group count, never its driver count.
 *
 * Answers are kept for the life of the mounted screen; opening the screen again asks again.
 */
export const useDriverGroupBadges = (driverIds: ReadonlyArray<string | undefined> | 'all'): ReadonlyMap<string, TeamGroupSummary> => {
    const [badges, setBadges] = useState<ReadonlyMap<string, TeamGroupSummary>>(NO_BADGES)
    const asked = useRef(new Set<string>())
    const mounted = useRef(true)
    useEffect(() => {
        mounted.current = true
        return () => {
            mounted.current = false
        }
    }, [])

    const all = driverIds === 'all'
    useEffect(() => {
        // Guarded by the same `asked` set the id mode uses, so "one request" survives an effect that runs
        // twice — which React does in StrictMode.
        if (!all || asked.current.has(ALL)) return
        asked.current.add(ALL)
        // NO per-run cancel flag, deliberately, and this is the whole subtlety: under StrictMode's
        // mount → cleanup → mount, a flag set in the cleanup would discard the first run's answer while
        // the guard above stops the second run from fetching at all. The result is no badges whatsoever,
        // and nothing logged to say so. `mounted` is the only thing that needs saying, and it is already
        // tracked above — it ends true after a remount, which is exactly when the answer should land.
        void fetchDriverTeamGroupBadges()
            .then((found) => {
                if (mounted.current) setBadges(new Map(Object.entries(found)))
            })
            .catch((error) => {
                // Said out loud: no badge on any row looks exactly like "nobody is in a group".
                console.error('[box-truck] driver group badges failed — rows are drawn without them', { scope: 'all', error })
                asked.current.delete(ALL)
            })
    }, [all])

    // A stable key, so a list that re-renders with the same drivers does not ask again.
    const idsKey = useMemo(() => (all ? '' : [...new Set(driverIds.filter((id): id is string => !!id))].sort().join(',')), [all, driverIds])

    useEffect(() => {
        if (all) return
        const missing = idsKey ? idsKey.split(',').filter((id) => !asked.current.has(id)) : []
        if (missing.length === 0) return
        for (const id of missing) asked.current.add(id)
        for (let start = 0; start < missing.length; start += BADGE_REQUEST_SIZE) {
            const chunk = missing.slice(start, start + BADGE_REQUEST_SIZE)
            void fetchDriverTeamGroupBadges(chunk)
                .then((found) => {
                    if (!mounted.current || Object.keys(found).length === 0) return
                    setBadges((current) => new Map([...current, ...Object.entries(found)]))
                })
                .catch((error) => {
                    // Said out loud: no badge on any row looks exactly like "nobody is in a group".
                    console.error('[box-truck] driver group badges failed — rows are drawn without them', { drivers: chunk.length, error })
                    // Forget these ids so the next change of the list asks again.
                    for (const id of chunk) asked.current.delete(id)
                })
        }
    }, [all, idsKey])

    return badges
}
